import React, { useCallback } from 'react';
import { Marker, Polyline } from '@react-google-maps/api';

import { FormattedMarker } from '@atom/types/map';

type Props = {
  marker: FormattedMarker;
  onClick: (marker: FormattedMarker) => void;
};

const arePropsEqual = (prev: Props, next: Props): boolean => {
  if (prev.onClick !== next.onClick) {
    return false;
  }

  const prevMarker = prev.marker;
  const nextMarker = next.marker;

  return (
    prevMarker.id === nextMarker.id &&
    prevMarker.isActive === nextMarker.isActive &&
    prevMarker.disabled === nextMarker.disabled &&
    prevMarker.clickable === nextMarker.clickable &&
    prevMarker.color === nextMarker.color &&
    prevMarker.zIndex === nextMarker.zIndex &&
    prevMarker.icon.url === nextMarker.icon.url &&
    prevMarker.position.lat === nextMarker.position.lat &&
    prevMarker.position.lng === nextMarker.position.lng &&
    prevMarker.path === nextMarker.path
  );
};

const MapShapeMemo = ({ marker, onClick }: Props) => {
  const opacity = marker.disabled ? 0.5 : 1;
  const isActive = marker.isActive === true;
  const path = marker.path;
  const color = marker.color;
  const clickable = marker.clickable ?? true;

  const handleClick = useCallback(() => onClick(marker), [onClick, marker]);

  return (
    <>
      <Marker
        position={marker.position}
        // @ts-expect-error
        icon={marker.icon}
        clickable={clickable}
        onClick={handleClick}
        zIndex={marker.zIndex || (isActive ? 502 : 498)}
        opacity={opacity}
      />
      {path && (
        <>
          <Polyline
            path={path}
            options={{
              strokeWeight: isActive ? 10 : 3,
              strokeColor: color,
              zIndex: isActive ? 500 : 496,
              clickable,
            }}
            onClick={handleClick}
          />
          {isActive && (
            <>
              <Polyline
                path={path}
                options={{
                  strokeColor: '#FFFFFF',
                  strokeWeight: 14,
                  zIndex: 499,
                  clickable,
                }}
              />
              <Polyline
                path={path}
                options={{
                  strokeColor: '#1E88E5',
                  strokeWeight: 18,
                  strokeOpacity: 0.5,
                  zIndex: 498,
                  clickable,
                }}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default React.memo(MapShapeMemo, arePropsEqual);
